import { useEffect } from 'react';
import { AuditTimeline } from './AuditTimeline';
import { useRowAuditLog } from './useRowAuditLog';

// Master-grid "History" modal (P2b-2 §7.5.3). Hosts the same AuditTimeline
// component the row editor uses — one timeline, two render hosts.
//
// Close via: backdrop click, Escape, or the × button. No Confirm/Cancel
// split because this is a read-only surface; dismissal is the only user
// action.

type Props = {
  rolloutId: string;
  rowId: string;
  locationName: string;
  onClose: () => void;
};

export function RowHistoryModal({ rolloutId, rowId, locationName, onClose }: Props) {
  const audit = useRowAuditLog(rolloutId, rowId);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[80vh] flex flex-col rounded-md bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="row-history-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 id="row-history-title" className="text-lg font-semibold text-slate-900">
            History for {locationName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-700 text-xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-4 flex-1">
          <AuditTimeline
            entries={audit.data}
            isLoading={audit.isLoading}
            error={audit.error}
          />
        </div>
      </div>
    </div>
  );
}
